import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, catchError, map, throwError } from 'rxjs';
import { AppErrors } from '@fitness-force/models';
import {
  CommissionPlanSlabResponse,
  CreateCommissionPlanSlabRequest,
  UpdateCommissionPlanSlabRequest,
  ListCommissionPlanSlabsResponse,
  ListCommissionPlanSlabsRequest,
  CreateResponseData,
  UpdateResponseData
} from '../models';
import { environment } from '@fitness-force/environments';
import { CONST_URL_SEGEMENTS } from '@fitness-force/constants';

@Injectable({
  providedIn: 'root'
})
export class CommissionPlanSlabService {
  private http = inject(HttpClient);

  private readonly API_BASE_URL = environment.APP_URL;

  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    })
  };

  private buildParams(params: Record<string, unknown>): HttpParams {
    let httpParams = new HttpParams();
    
    Object.keys(params).forEach(key => {
      const value = params[key];
      if (value !== null && value !== undefined) {
        httpParams = httpParams.set(key, value.toString());
      }
    });
    
    return httpParams;
  }

  // Get all slabs for a commission plan rule
  getCommissionPlanSlabs(planId: number, ruleId: number, request?: ListCommissionPlanSlabsRequest): Observable<ListCommissionPlanSlabsResponse> {
    const params: Record<string, unknown> = {
      PageNumber: request?.page || 1,
      PageSize: request?.page_size || 10
    };
    
    if (request?.active_only !== undefined) {
      params['ActiveOnly'] = request.active_only;
    }
    
    return this.http.get<ListCommissionPlanSlabsResponse>(
      this.API_BASE_URL + CONST_URL_SEGEMENTS.COMMISSION.GET_COMPANY_PLAN_RULE_SLABS
        .replace('{plan_id}', planId.toString())
        .replace('{rule_id}', ruleId.toString()),
      {
        headers: this.httpOptions.headers,
        params: this.buildParams(params)
      }
    ).pipe(
      map((res) => res),
      catchError((error) => {
        return throwError(() => new AppErrors(error));
      })
    );
  }

  // Get a specific slab by ID
  getCommissionPlanSlab(planId: number, ruleId: number, slabId: number): Observable<CommissionPlanSlabResponse> {
    return this.http.get<CommissionPlanSlabResponse>(
      this.API_BASE_URL + CONST_URL_SEGEMENTS.COMMISSION.GET_COMPANY_PLAN_RULE_SLAB_BY_ID
        .replace('{plan_id}', planId.toString())
        .replace('{rule_id}', ruleId.toString())
        .replace('{slab_id}', slabId.toString()),
      {
        headers: this.httpOptions.headers
      }
    ).pipe(
      map((res) => res),
      catchError((error) => {
        return throwError(() => new AppErrors(error));
      })
    );
  }

  // Create a new slab
  createCommissionPlanSlab(planId: number, ruleId: number, request: CreateCommissionPlanSlabRequest): Observable<CreateResponseData> {
    return this.http.post<CreateResponseData>(
      this.API_BASE_URL + CONST_URL_SEGEMENTS.COMMISSION.CREATE_COMPANY_PLAN_RULE_SLAB
        .replace('{plan_id}', planId.toString())
        .replace('{rule_id}', ruleId.toString()),
      request,
      {
        headers: this.httpOptions.headers
      }
    ).pipe(
      map((res) => res),
      catchError((error) => {
        return throwError(() => new AppErrors(error));
      })
    );
  }

  // Update an existing slab
  updateCommissionPlanSlab(planId: number, ruleId: number, slabId: number, request: UpdateCommissionPlanSlabRequest): Observable<UpdateResponseData> {
    return this.http.put<UpdateResponseData>(
      this.API_BASE_URL + CONST_URL_SEGEMENTS.COMMISSION.UPDATE_COMPANY_PLAN_RULE_SLAB
        .replace('{plan_id}', planId.toString())
        .replace('{rule_id}', ruleId.toString())
        .replace('{slab_id}', slabId.toString()),
      request,
      {
        headers: this.httpOptions.headers
      }
    ).pipe(
      map((res) => res),
      catchError((error) => {
        return throwError(() => new AppErrors(error));
      })
    );
  }

  // Deactivate a slab
  deactivateCommissionPlanSlab(planId: number, ruleId: number, slabId: number): Observable<void> {
    return this.http.delete<void>(
      this.API_BASE_URL + CONST_URL_SEGEMENTS.COMMISSION.DELETE_COMPANY_PLAN_RULE_SLAB
        .replace('{plan_id}', planId.toString())
        .replace('{rule_id}', ruleId.toString())
        .replace('{slab_id}', slabId.toString()),
      {
        headers: this.httpOptions.headers
      }
    ).pipe(
      map((res) => res),
      catchError((error) => {
        return throwError(() => new AppErrors(error));
      })
    );
  }

  // Get active slabs only
  getActiveCommissionPlanSlabs(planId: number, ruleId: number): Observable<ListCommissionPlanSlabsResponse> {
    return this.http.get<ListCommissionPlanSlabsResponse>(this.API_BASE_URL + CONST_URL_SEGEMENTS.COMPANIES.GET_ACTIVE_COMMISSION_PLAN_SLABS
      .replace('{plan_id}', planId.toString())
      .replace('{rule_id}', ruleId.toString()), {
      headers: this.httpOptions.headers
    }).pipe(
      map((res) => res),
      catchError((error) => {
        return throwError(() => new AppErrors(error));
      })
    );
  }
}

